import * as React from "react";
import Backdrop from "@mui/material/Backdrop";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import Fade from "@mui/material/Fade";
import styled from "styled-components";
import { Flex, Button, ButtonClass } from "src/components/ui";
import closeIcon from "src/assets/images/common/closeIcon.svg";
import { theme } from "src/styles/Theme";
import { toast } from "react-toastify";
import { useFilterOptionsQuery } from "src/store/services/adminApi";

const Wrapper = styled.div`
  .heading {
    background-color: ${(props) => props.theme.colors.gray_04};
    padding: 10px 20px;
    height: 56px;
    align-items: center;
    display: flex;
    border-radius: 8px 8px 0 0;

    img {
      padding: 8px;
      &:hover {
        cursor: pointer;
        background-color: ${(props) => props.theme.colors.lilac};
        transition: 0.3s;
        border-radius: 50%;
      }
    }

    p {
      font-weight: 700;
      font-size: 16px;
      color: ${(props) => props.theme.colors.text_01};
    }
  }

  .content {
    border-radius: 0 0 8px 8px;
    padding: 20px;

    .label {
      font-size: 14px;
      color: ${(props) => props.theme.colors.text_01};
      margin-bottom: 5px;
    }

    select {
      padding: 10px 14px;
      border: 1px solid ${(props) => props.theme.colors.text_02};
      background-color: ${(props) => props.theme.colors.gray_04};
      color: ${(props) => props.theme.colors.text_01};
      font-size: 16px;
      border-radius: 8px;
      width: 100%;
      height: 44px;
      margin-bottom: 15px;
      outline: none;

      &:focus {
        border: 1px solid ${(props) => props.theme.colors.purple};
      }
    }
  }
`;

const style = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 532,
  maxWidth: "90%",
  bgcolor: theme.colors.white,
  borderRadius: "8px",
  boxShadow: 24,
  outline: "none",
};

interface IFilterData {
  tier: number | string;
  country: string[]; // ✅ CHANGED
  states: string[]; // ✅ CHANGED
  gender: string;
  occupation: string;
  has_gallery: boolean | "";
  incomplete_registration: boolean | "";
  last_activity: string;
  app_version: {
    include: string;
    exclude: string;
  };
}

const initialFilterState: IFilterData = {
  tier: "",
  country: [], // ✅ CHANGED
  states: [], // ✅ CHANGED
  gender: "",
  occupation: "",
  has_gallery: "",
  incomplete_registration: "",
  last_activity: "",
  app_version: {
    include: "",
    exclude: "",
  },
};

export const FilterNotificationModal: React.FC<{
  open: boolean;
  handleClose: () => void;
  user: string;
  setFilterState: React.Dispatch<React.SetStateAction<IFilterData>>;
}> = ({ open, handleClose, user, setFilterState }) => {
  const [filterPayload, setFilterPayload] =
    React.useState<IFilterData>(initialFilterState);

  const {
    data: filterOptions = {
      countries: {},
      occupations: [],
      tiers: [],
      gender: [],
      has_gallery: [],
      incomplete_registration: [],
      lastLoginRange: [],
      app_version: { include: "", exclude: "" },
    },
    isLoading,
    isError,
  } = useFilterOptionsQuery(user === "customer" ? "user" : "artisan");

  React.useEffect(() => {
    if (isError) toast.error("Failed to load filter options");
  }, [isError]);

  // ===============================
  // COUNTRIES + STATES
  // ===============================
  const countriesObj = filterOptions?.countries || {};
  const countryList = Object.keys(countriesObj);
  // ===============================
  // APP VERSION OPTIONS
  // ===============================
  const appVersionOptions = [
    filterOptions?.app_version?.include,
    filterOptions?.app_version?.exclude,
  ].filter(Boolean);

  // merge states from ALL selected countries 
  const stateList = filterPayload.country
    .flatMap((c) => countriesObj[c]?.states || [])
    .filter((v, i, arr) => arr.indexOf(v) === i); // unique

  // ===============================
  // MULTI SELECT HANDLER
  // ===============================
  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;

    // ===============================
    // APP VERSION HANDLER
    // ===============================
    if (name === "app_version_include") {
      setFilterPayload((prev) => ({
        ...prev,
        app_version: {
          ...prev.app_version,
          include: value,
        },
      }));
      return;
    }

    if (name === "app_version_exclude") {
      setFilterPayload((prev) => ({
        ...prev,
        app_version: {
          ...prev.app_version,
          exclude: value,
        },
      }));
      return;
    }

    let parsedValue: any = value;

    if (name === "has_gallery" || name === "incomplete_registration") {
      parsedValue = value === "" ? "" : value === "true";
    }

    // ===============================
    // COUNTRY MULTI-SELECT TOGGLE
    // ===============================
    if (name === "country") {
      setFilterPayload((prev) => {
        const exists = prev.country.includes(value);

        return {
          ...prev,
          country: exists
            ? prev.country.filter((c) => c !== value)
            : [...prev.country, value],
          states: [],
        };
      });
      return;
    }

    // ===============================
    // STATE MULTI-SELECT TOGGLE
    // ===============================
    if (name === "states") {
      setFilterPayload((prev) => {
        const exists = prev.states.includes(value);

        return {
          ...prev,
          states: exists
            ? prev.states.filter((s) => s !== value)
            : [...prev.states, value],
        };
      });
      return;
    }

    setFilterPayload((prev) => ({
      ...prev,
      [name]: parsedValue,
    }));
  };

  const saveHandler = (e: any) => {
    e.preventDefault();
    setFilterState(filterPayload);
    resetHandler();
    handleClose();
  };

  const resetHandler = () => {
    setFilterPayload(initialFilterState);
  };
  if (isLoading) return null;

  return (
    <Modal
      open={open}
      onClose={handleClose}
      closeAfterTransition
      BackdropComponent={Backdrop}
    >
      <Fade in={open}>
        <Box sx={style}>
          <Wrapper>
            <div className="heading">
              <Flex
                justify="space-between"
                align="center"
                style={{ width: "100%" }}
              >
                <p>Filter</p>
                <img src={closeIcon} alt="x" onClick={handleClose} />
              </Flex>
            </div>

            <div className="content">
              <Flex direction={user === "customer" ? "column" : "row"}>
                <div>
                  {/* Tier */}
                  <p className="label">Tier</p>
                  <select
                    name="tier"
                    value={filterPayload.tier}
                    onChange={handleSelectChange}
                  >
                    <option value="">Select all</option>
                    {filterOptions.tiers.map((tier: number) => (
                      <option key={tier} value={tier}>
                        {tier}
                      </option>
                    ))}
                  </select>

                  {/* Gender */}
                  <p className="label">Gender</p>
                  <select
                    name="gender"
                    value={filterPayload.gender}
                    onChange={handleSelectChange}
                  >
                    <option value="">Select all</option>
                    {filterOptions.gender.map((g: string) => (
                      <option key={g} value={g}>
                        {g}
                      </option>
                    ))}
                  </select>

                  {/* Occupation */}
                  {user === "artisan" && (
                    <>
                      <p className="label">Occupation</p>
                      <select
                        name="occupation"
                        value={filterPayload.occupation}
                        onChange={handleSelectChange}
                      >
                        <option value="">Select all</option>
                        {filterOptions.occupations.map((o: string) => (
                          <option key={o} value={o}>
                            {o}
                          </option>
                        ))}
                      </select>

                      {/* Gallery */}
                      <p className="label">Gallery Post</p>
                      <select
                        name="has_gallery"
                        value={
                          filterPayload.has_gallery === ""
                            ? ""
                            : String(filterPayload.has_gallery)
                        }
                        onChange={handleSelectChange}
                      >
                        <option value="">Select all</option>
                        {filterOptions.has_gallery.map((val: boolean) => (
                          <option key={String(val)} value={String(val)}>
                            {val ? "Gallery Post" : "No Gallery Post"}
                          </option>
                        ))}
                      </select>
                    </>
                  )}

                  {/* Last Activity */}
                  <p className="label">Last Activity</p>
                  <select
                    name="last_activity"
                    value={filterPayload.last_activity}
                    onChange={handleSelectChange}
                  >
                    <option value="">Select all</option>
                    {filterOptions.lastLoginRange.map((range: string) => (
                      <option key={range} value={range}>
                        {range}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  {/* Complete Registration */}
                  <p className="label">Complete Registration</p>
                  <select
                    name="incomplete_registration"
                    value={
                      filterPayload.incomplete_registration === ""
                        ? ""
                        : String(filterPayload.incomplete_registration)
                    }
                    onChange={handleSelectChange}
                  >
                    <option value="">Select all</option>
                    {filterOptions.incomplete_registration.map(
                      (val: boolean) => (
                        <option key={String(val)} value={String(val)}>
                          {val ? "No" : "Yes"}
                        </option>
                      ),
                    )}
                  </select>

                  {/* COUNTRY (MULTI) */}
                  <p className="label">
                    Country{" "}
                    {filterPayload.country.length > 0 &&
                      `(${filterPayload.country.join(", ")})`}
                  </p>
                  <select name="country" value="" onChange={handleSelectChange}>
                    <option value="">Select country</option>
                    {countryList.map((key: string) => (
                      <option key={key} value={key}>
                        {countriesObj[key].label}
                      </option>
                    ))}
                  </select>

                  <p className="label">App Version (Include)</p>
                  <input
                    name="app_version_include"
                    className="border-2 border-gray-400 rounded-lg"
                    value={filterPayload.app_version.include}
                    onChange={(e) =>
                      setFilterPayload((prev) => ({
                        ...prev,
                        app_version: {
                          ...prev.app_version,
                          include: e.target.value,
                        },
                      }))
                    }
                  />

                  <p className="label">App Version (Exclude)</p>
                  <input
                    name="app_version_exclude"
                    className="border-2 border-gray-400 rounded-lg"
                    value={filterPayload.app_version.exclude}
                    onChange={(e) =>
                      setFilterPayload((prev) => ({
                        ...prev,
                        app_version: {
                          ...prev.app_version,
                          exclude: e.target.value,
                        },
                      }))
                    }
                  />

                  {/* STATE (MULTI) */}
                  <p className="label">
                    State{" "}
                    {filterPayload.states.length > 0 &&
                      `(${filterPayload.states.join(", ")})`}
                  </p>
                  <select
                    name="states"
                    value=""
                    onChange={handleSelectChange}
                    disabled={filterPayload.country.length === 0}
                  >
                    <option value="">Select state</option>
                    {stateList.map((s: string) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>

                  <Flex justify="space-between">
                    <Button
                      classes={[ButtonClass.NO_BG]}
                      onClick={resetHandler}
                    >
                      Reset
                    </Button>
                    <Button classes={[ButtonClass.SOLID]} onClick={saveHandler}>
                      Save
                    </Button>
                  </Flex>
                </div>
              </Flex>
            </div>
          </Wrapper>
        </Box>
      </Fade>
    </Modal>
  );
};

export default FilterNotificationModal;
